// ══════════════════════════════════════════════════════════════════════════════
// ApiInspector.tsx — Component for tracking and displaying API calls
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useLayoutEffect, useRef } from 'react';

interface ApiDoc {
  method: string;
  endpoint: string;
  requestBody?: string | null;
  response?: string | null;
}

interface ApiCall {
  method: string;
  endpoint: string;
  timestamp: Date;
  status: number | null;
  duration: number | null;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: string;
}

interface ApiInspectorProps {
  docs: readonly ApiDoc[];
}

const ApiInspector: React.FC<ApiInspectorProps> = ({ docs }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [calls, setCalls] = useState<Map<string, ApiCall>>(new Map());
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const originalFetch = useRef<typeof fetch | null>(null);

  // Setup fetch interceptor usando useLayoutEffect per garantire che l'interceptor
  // sia installato PRIMA che i useEffect dei componenti padre chiamino fetch
  useLayoutEffect(() => {
    // Save original fetch if not already saved
    if (!originalFetch.current) {
      originalFetch.current = window.fetch;
    }

    // Create endpoint map for quick lookup
    const endpointMap = new Map<string, ApiDoc>();
    docs.forEach(doc => {
      endpointMap.set(doc.endpoint, doc);
    });

    // Intercept fetch
    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : String(args[0]));
      const options = typeof args[0] === 'string' ? args[1] : (args[0] instanceof Request ? undefined : args[1]);

      // Extract endpoint path from full URL
      // Support Express-style params (:id) by converting to regex
      let endpointPath: string | null = null;
      let matchedDoc: ApiDoc | null = null;

      for (const doc of docs) {
        const pattern = doc.endpoint.replace(/:([^/]+)/g, '[^/]+');
        const regex = new RegExp(pattern + '(\\?|$)');
        const urlPath = url.replace(/^https?:\/\/[^/]+/, '');
        if (regex.test(urlPath)) {
          endpointPath = doc.endpoint;
          matchedDoc = doc;
          break;
        }
      }

      // If not a tracked endpoint, pass through
      if (!matchedDoc || !endpointPath) {
        return originalFetch.current!(...args);
      }

      const startTime = performance.now();
      const method = options?.method || 'GET';
      const key = `${method} ${endpointPath}`;

      let requestBody: unknown = undefined;
      if (options?.body) {
        try {
          if (typeof options.body === 'string') {
            requestBody = JSON.parse(options.body);
          } else {
            requestBody = options.body;
          }
        } catch {
          requestBody = options.body;
        }
      }

      try {
        const response = await originalFetch.current!(...args);
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        // Clone response to read body without consuming it
        const clonedResponse = response.clone();
        let responseBody: unknown = undefined;

        try {
          const contentType = clonedResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            responseBody = await clonedResponse.json();
          } else {
            responseBody = await clonedResponse.text();
          }
        } catch {
          responseBody = '[Unable to parse response]';
        }

        // Update state
        setCalls(prev => {
          const newMap = new Map(prev);
          newMap.set(key, {
            method,
            endpoint: endpointPath!,
            timestamp: new Date(),
            status: response.status,
            duration,
            requestBody,
            responseBody
          });
          return newMap;
        });

        return response;
      } catch (err) {
        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        setCalls(prev => {
          const newMap = new Map(prev);
          newMap.set(key, {
            method,
            endpoint: endpointPath!,
            timestamp: new Date(),
            status: null,
            duration,
            requestBody,
            error: err instanceof Error ? err.message : 'Network error'
          });
          return newMap;
        });

        throw err;
      }
    };

    // Cleanup: restore original fetch on unmount
    return () => {
      if (originalFetch.current) {
        window.fetch = originalFetch.current;
      }
    };
  }, [docs]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleExpand = (key: string) => {
    setExpandedKey(expandedKey === key ? null : key);
  };

  const getStatusBadgeClass = (status: number | null | undefined): string => {
    if (status === null || status === undefined) return 'bop-badge bop-badge-neutral';
    if (status >= 200 && status < 300) return 'bop-badge bop-badge-success';
    if (status >= 400 && status < 500) return 'bop-badge bop-badge-warning';
    if (status >= 500) return 'bop-badge bop-badge-danger';
    return 'bop-badge bop-badge-neutral';
  };

  const formatTimestamp = (date: Date | null): string => {
    if (!date) return '—';
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getMethodBadgeClass = (method: string): string => {
    if (method === 'GET') return 'bop-badge bop-badge-info';
    if (method === 'POST') return 'bop-badge bop-badge-success';
    if (method === 'PUT') return 'bop-badge bop-badge-warning';
    if (method === 'DELETE') return 'bop-badge bop-badge-danger';
    return 'bop-badge bop-badge-neutral';
  };

  return (
    <div className="bop-card api-inspector-container">
      <div className="bop-card-header-with-action">
        <h3 className="bop-card-title">API Inspector</h3>
        <button onClick={toggleCollapse} className="bop-btn-secondary bop-btn-sm">
          {isCollapsed ? 'Espandi' : 'Comprimi'}
        </button>
      </div>

      {!isCollapsed && (
        <table className="bop-table">
          <thead>
            <tr>
              <th>Metodo</th>
              <th>Endpoint</th>
              <th>Ultima chiamata</th>
              <th>Status</th>
              <th>Tempo (ms)</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => {
              const key = `${doc.method} ${doc.endpoint}`;
              const call = calls.get(key);

              return (
                <React.Fragment key={key}>
                  <tr>
                    <td>
                      <span className={getMethodBadgeClass(doc.method)}>
                        {doc.method}
                      </span>
                    </td>
                    <td>{doc.endpoint}</td>
                    <td>{call ? formatTimestamp(call.timestamp) : '—'}</td>
                    <td>
                      {call ? (
                        call.status !== null ? (
                          <span className={getStatusBadgeClass(call.status)}>{call.status}</span>
                        ) : (
                          <span className="bop-badge bop-badge-danger">Error</span>
                        )
                      ) : (
                        <span className="bop-badge bop-badge-neutral">—</span>
                      )}
                    </td>
                    <td>{call && call.duration !== null ? `${call.duration}ms` : '—'}</td>
                    <td>
                      <button
                        onClick={() => toggleExpand(key)}
                        className="bop-btn-secondary bop-btn-sm"
                      >
                        {expandedKey === key ? 'Nascondi' : 'Dettagli'}
                      </button>
                    </td>
                  </tr>
                  {expandedKey === key && (
                    <tr>
                      <td colSpan={6} className="api-inspector-details">
                        {call ? (
                          <>
                            <div className="api-inspector-detail-section">
                              <h4 className="bop-label">Request Payload (runtime)</h4>
                              <pre className="api-inspector-json">
                                {call.requestBody ? JSON.stringify(call.requestBody, null, 2) : 'Nessun payload'}
                              </pre>
                            </div>
                            <div className="api-inspector-detail-section">
                              <h4 className="bop-label">Response Body (runtime)</h4>
                              {call.error ? (
                                <p className="bop-text-danger">{call.error}</p>
                              ) : (
                                <pre className="api-inspector-json">
                                  {call.responseBody ? JSON.stringify(call.responseBody, null, 2) : 'Nessuna risposta'}
                                </pre>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="api-inspector-detail-section">
                            <p className="bop-text-muted">Nessuna chiamata registrata</p>
                            {doc.requestBody && (
                              <div className="api-inspector-contract-section">
                                <h4 className="bop-label">Request atteso (contratto API)</h4>
                                <pre className="api-inspector-json">{doc.requestBody}</pre>
                              </div>
                            )}
                            {doc.response && (
                              <div className="api-inspector-contract-section">
                                <h4 className="bop-label">Response attesa (contratto API)</h4>
                                <pre className="api-inspector-json">{doc.response}</pre>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ApiInspector;
